import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiDownload, FiPrinter, FiExternalLink, FiFileText } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import { getTransactions } from '../../utils/transactionHelper';
import { formatAmount } from '../../utils/documentNumbering';
import { TRAN_TYPE_ROUTES } from '../../utils/navigationHelper';
import PageLayout from '../../components/PageLayout';
import useReportMemory from '../../hooks/useReportMemory';
import {
  TableContainer, Table, Th, Tr, Td, StatusBadge, LoadingMessage, TranEmptyState
} from '../../components/SharedStyles';
import { addPrintBrand } from '../../utils/printBranding';

const STATUS_COLORS = { Draft: colors.warning.main, Posted: colors.success.main, Cancelled: colors.error.main };

const ProductionReceiptReport = () => {
  const { activeCompany, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('production-receipt-report');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(savedMem ? false : true);
  const [receipts, setReceipts] = useState(() => savedMem?.receipts ?? []);
  const today = new Date().toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? monthAgo);
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? today);
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'All');
  const [search, setSearch] = useState(() => savedMem?.search ?? '');

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      const branchFilter = activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {};
      // Bounded server-side by the report's date pickers (Firebase cost) —
      // previously this read the entire PRC history every visit.
      const prc = await getTransactions(activeCompany.id, { tranTypeCode: 'PRC', ...branchFilter, fromDate: dateFrom, toDate: dateTo });
      setReceipts(prc);
    } catch (e) { console.error('Error loading production receipt report:', e); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch, dateFrom, dateTo]);

  // Skip the automatic first load when we restored a saved view (Back nav) so
  // Back shows the remembered rows with 0 re-reads; a later filter change still
  // re-fetches normally.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    load();
  }, [load]);

  // Remember this view (filters + loaded rows) so Back restores it (0 re-reads).
  useEffect(() => {
    if (loading) return;
    saveMemory({ dateFrom, dateTo, statusFilter, search, receipts });
  }, [dateFrom, dateTo, statusFilter, search, receipts, loading, saveMemory]);

  const rows = useMemo(() => {
    return receipts
      .filter(r => {
        if (statusFilter !== 'All' && r.status !== statusFilter) return false;
        if (r.docDate && (r.docDate < dateFrom || r.docDate > dateTo)) return false;
        if (search.trim()) {
          const q = search.toLowerCase();
          if (!(r.docNumber?.toLowerCase().includes(q) || r.issueDocNumber?.toLowerCase().includes(q) || r.locationName?.toLowerCase().includes(q) || r.notes?.toLowerCase().includes(q))) return false;
        }
        return true;
      })
      .map(r => {
        const qty = (r.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
        return { ...r, fgQty: qty, value: Number(r.totalValue) || 0 };
      })
      .sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
  }, [receipts, statusFilter, dateFrom, dateTo, search]);

  const kpis = useMemo(() => {
    const posted = rows.filter(r => r.status === 'Posted');
    return { count: rows.length, value: posted.reduce((s, r) => s + r.value, 0), qty: posted.reduce((s, r) => s + r.fgQty, 0) };
  }, [rows]);

  const open = (r) => { const route = TRAN_TYPE_ROUTES[r.tranTypeCode]; if (route) navigate(route, { state: { viewTransactionId: r.id } }); };

  const exportCsv = () => {
    let csv = `Production Receipt Report,${dateFrom} to ${dateTo}\n`;
    csv += `Doc No,Date,From Issue,Location,FG Qty,FG Cost,Status\n`;
    rows.forEach(r => { csv += `${r.docNumber || ''},${r.docDate || ''},${r.issueDocNumber || ''},"${(r.locationName || '').replace(/"/g, '""')}",${r.fgQty},${r.value},${r.status}\n`; });
    csv += `\nTotals,,,,"${kpis.qty}","${kpis.value}",\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `production-receipt-report.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const cur = activeCompany?.currencyCode || 'PKR';

  const handlePrint = () => {
    const rowsHtml = rows.map(r => `
      <tr>
        <td>${r.docNumber || ''}</td>
        <td>${r.docDate || ''}</td>
        <td>${r.issueDocNumber || '-'}</td>
        <td>${r.locationName || '-'}</td>
        <td class="amt">${formatAmount(r.fgQty)}</td>
        <td class="amt">${formatAmount(r.value)}</td>
        <td>${r.status}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Production Receipt Report</title><style>
      body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
      h1{color:#800020;font-size:20px;margin-bottom:5px}
      .meta{color:#666;font-size:12px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse}
      th{background:#800020;color:#fff;padding:8px;text-align:left;font-size:11px;text-transform:uppercase}
      td{padding:7px 8px;border-bottom:1px solid #ddd}
      .amt{text-align:right;font-family:Consolas,monospace}
      tfoot td{font-weight:700;border-top:2px solid #800020}
      @media print{body{margin:12px}}
    </style></head><body>
      <h1>Production Receipt Report</h1>
      <div class="meta">${activeCompany?.companyName || 'Company'} | Period: ${dateFrom} to ${dateTo}${activeBranch && activeBranch.id !== 'all' ? ' | Branch: ' + (activeBranch.branchName || '') : ''}</div>
      <table>
        <thead><tr><th>Doc No</th><th>Date</th><th>From Issue</th><th>Location</th><th class="amt">FG Qty</th><th class="amt">FG Cost</th><th>Status</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr><td colspan="4">Totals (Posted)</td><td class="amt">${formatAmount(kpis.qty)}</td><td class="amt">${formatAmount(kpis.value)}</td><td></td></tr></tfoot>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Production Receipt Report" breadcrumb="Reports / Production Receipt">
      <FiltersCard>
        <Field><label>From</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></Field>
        <Field><label>To</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></Field>
        <Field><label>Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All</option><option value="Draft">Draft</option><option value="Posted">Posted</option><option value="Cancelled">Cancelled</option>
          </select>
        </Field>
        <Field style={{ flex: 1 }}><label>Search</label>
          <SearchWrap><FiSearch /><input type="text" placeholder="Doc no, issue, location..." value={search} onChange={e => setSearch(e.target.value)} /></SearchWrap>
        </Field>
        <Btn onClick={exportCsv}><FiDownload /> CSV</Btn>
        <Btn onClick={handlePrint}><FiPrinter /> Print</Btn>
      </FiltersCard>

      <KpiGrid>
        <Kpi><span>{kpis.count}</span><label>Receipts</label></Kpi>
        <Kpi><span>{cur} {formatAmount(kpis.value)}</span><label>Finished Goods Cost (Posted)</label></Kpi>
        <Kpi><span>{formatAmount(kpis.qty)}</span><label>Total FG Quantity (Posted)</label></Kpi>
      </KpiGrid>

      {loading ? <LoadingMessage>Loading...</LoadingMessage> : rows.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Production Receipts</h3><p>No records match the selected filters.</p></TranEmptyState>
      ) : (
        <TableContainer><Table><thead><tr>
          <Th>Doc No</Th><Th>Date</Th><Th>From Issue</Th><Th>Location</Th>
          <Th style={{ textAlign: 'right' }}>FG Qty</Th>
          <Th style={{ textAlign: 'right' }}>FG Cost</Th>
          <Th style={{ textAlign: 'center' }}>Status</Th>
        </tr></thead><tbody>
          {rows.map(r => (
            <Tr key={r.id}>
              <Td><DocLink onClick={() => open(r)}>{r.docNumber} <FiExternalLink size={11} /></DocLink></Td>
              <Td>{r.docDate}</Td>
              <Td>{r.issueDocNumber || '-'}</Td>
              <Td>{r.locationName || '-'}</Td>
              <Td style={{ textAlign: 'right' }}>{formatAmount(r.fgQty)}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(r.value)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[r.status]}>{r.status}</StatusBadge></Td>
            </Tr>
          ))}
        </tbody></Table></TableContainer>
      )}
    </PageLayout>
  );
};

const FiltersCard = styled.div`background:white;border-radius:12px;box-shadow:${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};padding:16px;margin-bottom:16px;display:flex;gap:12px;align-items:flex-end;flex-wrap:wrap;`;
const Field = styled.div`display:flex;flex-direction:column;gap:4px;label{font-size:11px;color:${colors.text.secondary};font-weight:600;text-transform:uppercase;}input,select{padding:8px 10px;border:1px solid ${colors.neutral[300]};border-radius:6px;font-size:13px;}`;
const SearchWrap = styled.div`display:flex;align-items:center;gap:6px;border:1px solid ${colors.neutral[300]};border-radius:6px;padding:0 8px;svg{color:${colors.text.secondary};}input{border:none;outline:none;padding:8px 0;width:100%;}`;
const Btn = styled.button`display:inline-flex;align-items:center;gap:6px;padding:9px 14px;background:${colors.primary.main};color:white;border:none;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;&:hover{background:${colors.primary.dark};}`;
const KpiGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;margin-bottom:16px;`;
const Kpi = styled.div`background:white;border-radius:12px;box-shadow:${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};padding:16px;border-top:3px solid ${colors.primary.main};span{display:block;font-size:20px;font-weight:700;color:${colors.text.primary};}label{font-size:12px;color:${colors.text.secondary};}`;
const DocLink = styled.span`color:${colors.primary.main};font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:4px;&:hover{text-decoration:underline;}`;

export default ProductionReceiptReport;
